/**
 * Merchant Shops — owners can self-onboard a new shop AND edit their
 * existing shops. The "Add shop" form is the SAME as the Tenant Admin's
 * Add Shop form (shared `<ShopFormFields>` component) — admin-only
 * fields (commission override, status picker, referral code) are
 * hidden. New shops land in `PENDING_APPROVAL`; merchant can keep
 * setting up categories/menu while waiting.
 */
import { useEffect, useState } from "react";
import { Plus, Pencil, Loader2, Store, Clock4, CheckCircle2, XCircle, Lock } from "lucide-react";
import { api } from "../../lib/api";
import { Field, Modal } from "../admin/AdminRestaurants";
import ShopFormFields, {
    EMPTY_SHOP_FORM, shopFormToPayload, shopRowToForm,
} from "../../components/ShopFormFields";
import { isFoodShop } from "../../lib/foodCategory";
import { toast } from "sonner";
import { useTenant } from "../../contexts/TenantContext";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_META = {
    ACTIVE: { Icon: CheckCircle2, label: "Active", classes: "bg-emerald-100 text-emerald-700" },
    PENDING_APPROVAL: { Icon: Clock4, label: "Awaiting approval", classes: "bg-amber-100 text-amber-800" },
    REJECTED: { Icon: XCircle, label: "Rejected", classes: "bg-red-100 text-red-700" },
    INACTIVE: { Icon: XCircle, label: "Inactive", classes: "bg-muted text-muted-foreground" },
};

export default function MerchantShops() {
    const { tenant } = useTenant();
    const [rows, setRows] = useState([]);
    const [verticals, setVerticals] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(EMPTY_SHOP_FORM);
    const [busy, setBusy] = useState(false);
    // iter181 — a branch (zone) admin files a new shop under one of their own branches: the only one, or the one
    // picked here when they manage several (the server refuses any other). Owners and admins never see this.
    const { user } = useAuth() || {};
    const isBranchAdmin = user?.role === "ZONE_ADMIN";
    const [branches, setBranches] = useState(null);
    const [branchId, setBranchId] = useState("");
    const needsBranchPick = isBranchAdmin && !edit && (branches || []).length > 1;
    const noBranch = isBranchAdmin && !edit && branches !== null && branches.length === 0;
    const chosenBranch = branchId || ((branches || []).length === 1 ? branches[0].id : "");

    // iter104 — single-vendor tenants may host only ONE shop. Once the
    // merchant has (or is awaiting approval on) their first shop, we
    // hide the "+ Add Shop" CTA and surface an inline lock notice. The
    // backend enforces the same rule (POST /merchant/restaurants → 400)
    // so this is purely a UX guard.
    const isSingleVendor = (tenant?.vendor_mode || "MULTI_VENDOR") === "SINGLE_VENDOR";
    const singleVendorCapReached = isSingleVendor && rows.length > 0;

    const load = () => api.get("/merchant/restaurants").then(({ data }) => setRows(data || []));
    useEffect(() => {
        load();
        // Use the public business-categories list (not the admin-only one) so
        // merchants can read the available verticals without elevated privilege.
        api.get("/business-categories")
            .then(({ data }) => setVerticals(data || []))
            .catch(() => setVerticals([]));
    }, []);
    useEffect(() => {
        if (!isBranchAdmin) return undefined;
        let off = false;
        api.get("/zone-admin/me")
            .then(({ data }) => { if (!off) setBranches((data?.zones || []).map((z) => ({ id: z.id, name: z.name || "Branch" }))); })
            .catch(() => { if (!off) setBranches([]); });
        return () => { off = true; };
    }, [isBranchAdmin]);

    const openCreate = () => {
        setEdit(null); setForm(EMPTY_SHOP_FORM);
        setBranchId("");
        setOpen(true);
    };
    const openEdit = (r) => { setEdit(r); setForm(shopRowToForm(r)); setOpen(true); };

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Shop name is required"); return; }
        if (!form.business_category_ids || form.business_category_ids.length === 0) {
            toast.error("Pick at least one business category for the shop.");
            return;
        }
        if (needsBranchPick && !chosenBranch) { toast.error("Choose the branch this shop belongs to."); return; }
        setBusy(true);
        try {
            // Decide veg/non-veg gating using the shared isFoodShop() check —
            // same helper ShopFormFields uses for the picker itself. This
            // used to be a hand-rolled regex against the vertical's name/
            // slug/title (matching only "food|restaurant|cafe|bakery"),
            // which silently missed "meat"/"grocery" verticals (no food_type
            // saved for those shops) despite isFoodShop treating them as
            // food, and could false-positive on unrelated category names.
            const showFoodType = isFoodShop(
                { business_category_ids: form.business_category_ids || [] },
                verticals,
            );
            const payload = shopFormToPayload(form, { showFoodType, isAdmin: false });
            if (isBranchAdmin && !edit && chosenBranch) payload.zone_id = chosenBranch;
            if (edit) {
                await api.patch(`/merchant/restaurants/${edit.id}`, payload);
                toast.success("Shop updated");
            } else {
                await api.post("/merchant/restaurants", payload);
                toast.success("Shop submitted for approval");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save shop");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="merchant-shops">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Your Shops</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} shop{rows.length === 1 ? "" : "s"} — new shops need tenant admin approval before going live.</p>
                </div>
                {singleVendorCapReached ? (
                    <div
                        data-testid="merchant-single-vendor-locked"
                        className="inline-flex items-center gap-2 bg-muted text-muted-foreground rounded-xl px-3 py-2 text-xs font-medium border border-border"
                        title="This tenant is configured as single-vendor. Contact your platform admin to switch to multi-vendor if you need additional shops."
                    >
                        <Lock size={14} /> Single-vendor mode
                    </div>
                ) : (
                    <button data-testid="merchant-add-shop" onClick={openCreate} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> Add Shop</button>
                )}
            </div>

            {singleVendorCapReached && (
                <p
                    data-testid="single-vendor-notice"
                    className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900"
                >
                    This platform is running in <strong>single-vendor mode</strong>. Only one shop is allowed per merchant on this tenant. Contact your platform admin if you need to add more shops.
                </p>
            )}

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map((s) => {
                    const meta = STATUS_META[s.status] || STATUS_META.INACTIVE;
                    return (
                        <div key={s.id} data-testid={`merchant-shop-${s.id}`} className="bg-card border border-border rounded-2xl p-4">
                            <div className="flex items-start gap-3">
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                    {s.logo_url ? <img src={s.logo_url} alt={s.name} className="w-full h-full object-cover" /> : <Store size={20} className="text-primary" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold truncate">{s.name}</p>
                                    {s.description && <p className="text-xs text-muted-foreground line-clamp-1">{s.description}</p>}
                                </div>
                                <button data-testid={`merchant-shop-edit-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><Pencil size={14} /></button>
                            </div>
                            <div className="mt-3 flex items-center gap-1.5">
                                <span data-testid={`shop-status-${s.id}`} className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${meta.classes}`}>
                                    <meta.Icon size={11} /> {meta.label}
                                </span>
                            </div>
                            {s.status === "REJECTED" && s.rejection_reason && (
                                <p className="mt-2 text-xs text-red-700 bg-red-50 rounded-md px-2 py-1.5">Reason: {s.rejection_reason}</p>
                            )}
                            {s.status === "PENDING_APPROVAL" && (
                                <p className="mt-2 text-[11px] text-muted-foreground">You can keep setting up Categories and Menu — customers will see it once the tenant admin approves.</p>
                            )}
                        </div>
                    );
                })}
                {rows.length === 0 && (
                    <div className="col-span-full p-10 text-center text-muted-foreground text-sm bg-card border border-dashed border-border rounded-2xl">
                        {isSingleVendor
                            ? "No shop yet. Tap “Add Shop” to submit your single storefront for approval."
                            : "No shops yet. Tap “Add Shop” to submit one for approval."}
                    </div>
                )}
            </div>

            {open && (
                <Modal onClose={() => setOpen(false)} title={edit ? `Edit · ${edit.name}` : "Submit new shop for approval"}>
                    {isBranchAdmin && !edit && (
                        <div className="mb-3" data-testid="shop-branch">
                            {needsBranchPick ? (
                                <Field label="Branch">
                                    <select data-testid="shop-branch-select" className="inp" required value={branchId}
                                        onChange={(e) => setBranchId(e.target.value)}>
                                        <option value="">Choose a branch…</option>
                                        {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </Field>
                            ) : noBranch ? (
                                <p data-testid="shop-branch-none" className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-900">
                                    No branch is linked to your account yet. Ask the business admin to link one before adding a shop.
                                </p>
                            ) : (branches || []).length === 1 ? (
                                <p data-testid="shop-branch-fixed" className="text-xs text-muted-foreground">
                                    Branch: <span className="font-semibold text-foreground">{branches[0].name}</span>
                                </p>
                            ) : null}
                        </div>
                    )}
                    <ShopFormFields
                        form={form}
                        setForm={setForm}
                        edit={edit}
                        verticals={verticals}
                        isAdmin={false}
                    />
                    {!edit && (
                        <p className="text-[11px] text-muted-foreground mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2">
                            After submission, your tenant admin reviews this shop. You can add categories and products straight away — they remain hidden until the shop is approved.
                        </p>
                    )}
                    <button data-testid="merchant-form-save"
                        disabled={busy || !form.name.trim() || (needsBranchPick && !chosenBranch) || noBranch || (isBranchAdmin && !edit && branches === null)}
                        onClick={submit}
                        className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy && <Loader2 size={16} className="animate-spin" />} {edit ? "Save changes" : "Submit for approval"}
                    </button>
                </Modal>
            )}
        </div>
    );
}
